import type { ReactNode } from 'react';

import { cn } from '@core/lib/cn';

import { PageGlyph } from './PageGlyph';

export interface EmptyStateProps {
  /** What is empty, phrased as a fact: "No orders yet". */
  title: string;
  /** Why it is empty and what to do next. One or two sentences. */
  description?: ReactNode;
  /** The one action that resolves the emptiness. */
  action?: ReactNode;
  /**
   * Decorative mark. Defaults to the inline-SVG sheet of paper.
   *
   * It used to default to the 📄 emoji, which renders as a tofu box on systems
   * whose emoji font lacks U+1F4C4 — seen for real on the "no orders yet"
   * screen. A caller can still pass an emoji, but the default cannot depend on
   * the customer's font.
   */
  glyph?: ReactNode;
  className?: string;
}

/**
 * "There is nothing here, and that is not an error."
 *
 * Distinct from ErrorState on purpose: an empty list after a successful request is
 * a normal outcome and must not look like a failure or offer a retry — retrying an
 * empty list just shows the same empty list.
 */
export function EmptyState({ title, description, action, glyph, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      <span aria-hidden="true" className="text-4xl text-ink-muted">
        {glyph ?? <PageGlyph className="h-10 w-10" />}
      </span>
      <h2 className="text-lg font-semibold text-ink">{title}</h2>
      {description ? <p className="max-w-sm text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
